import { REPORT_OPTION_COVER_LETTER, REPORT_OPTION_SUMMARY } from '../constants';
/**
 * Whether the cover-letter flag actually means anything on this row.
 *
 * `include_in_cover_letter` is only meaningful when the row is on the report:
 * `InspectionAttachment.cover_letter_included` is `report_included.where(include_in_cover_letter:
 * true)`, so a row with `show_on_report: false` is not in the cover letter whatever the flag says.
 *
 * The stale combination is reachable, not theoretical: unchecking Summary sends only
 * `{ showOnReport: false }` (each inline toggle commits ONE field), and the bulk action can do the
 * same, leaving `include_in_cover_letter: true` behind. Reading the raw flag then showed
 * "Cover letter" in the summary for a row that is not on the report, with the checkbox hidden so
 * the user could not clear it. Derived in one place so the cell, the summary, the filter keys and
 * the sort order cannot disagree. Caught by CodeRabbit on PR #23851.
 */
export function effectiveCoverLetter(showOnReport, includeInCoverLetter) {
    return showOnReport && includeInCoverLetter;
}
/** OR-matched against the Report options filter's selection. A row with neither matches nothing. */
export function reportOptionKeys(showOnReport, includeInCoverLetter) {
    const keys = [];
    if (showOnReport)
        keys.push(REPORT_OPTION_SUMMARY);
    if (effectiveCoverLetter(showOnReport, includeInCoverLetter))
        keys.push(REPORT_OPTION_COVER_LETTER);
    return keys;
}
/**
 * Display text for the Report options column, e.g. "Summary · Cover letter".
 *
 * The separator matches Building's visibility summary (` · `) so the two surfaces read the same.
 * A row with neither option renders a `Select` placeholder: the em dash it used to render read as
 * "no value exists" rather than as an empty control the user can open (requester, 2026-10-30).
 */
export function reportOptionsSummary(showOnReport, includeInCoverLetter, translateFn) {
    const parts = [];
    if (showOnReport)
        parts.push(translateFn('report_options.summary_short'));
    if (effectiveCoverLetter(showOnReport, includeInCoverLetter)) {
        parts.push(translateFn('report_options.cover_letter_short'));
    }
    return parts.length > 0 ? parts.join(' · ') : translateFn('report_options.placeholder');
}
/**
 * Sort comparator key, over the EFFECTIVE state so the order matches what the column renders.
 * Sorting on the raw flag would place a row above another whose summary looks identical.
 */
export function reportOptionsSortKey(showOnReport, includeInCoverLetter) {
    return (showOnReport ? 1 : 0) + (effectiveCoverLetter(showOnReport, includeInCoverLetter) ? 2 : 0);
}
